import { FormFieldTypes } from "types/enums";
import { lintPcre } from "utils/pcreLint";
import { hasUnescapedSlash, isValidRegex } from "utils/regexValidation";
import { z } from "zod";

/** Frontend format is compiled in the browser (input mask and pattern), so it has to be a valid JS regex. */
const frontendFormatSchema = z
    .string()
    .nullable()
    .refine((value) => !value || isValidRegex(value), "forms.general-validation.regex_invalid");

/** BE wraps the value into `/<value>/`, so a bare slash would end the pattern early. */
const validationRegexSchema = z
    .string()
    .nullable()
    .superRefine((value, ctx) => {
        if (!value) {
            return;
        }
        if (hasUnescapedSlash(value)) {
            ctx.addIssue({ code: "custom", message: "forms.general-validation.regex_slash" });
            return;
        }
        // BE runs PCRE, so the browser engine must not decide here: it would reject valid PCRE syntax like `(?P<n>a)`
        const [firstError] = lintPcre(value).errors;
        if (firstError) {
            ctx.addIssue({ code: "custom", message: `forms.general-validation.regex_${firstError}` });
        }
    });

export const formFieldSchema = z.object({
    id: z.string().optional(),
    system_name: z.string().min(1, "forms.general-validation.system_name_required"),
    is_required: z.boolean().optional(),
    is_hidden: z.boolean().optional(),
    is_global: z.boolean().optional(),
    visible_description: z.string().min(1, "forms.general-validation.visible_description_required"),
    doc_note: z.string().nullable(),
    type: z.nativeEnum(FormFieldTypes, {
        errorMap: () => ({ message: "forms.general-validation.invalid_field_type" }),
    }),
    default_value: z.string().nullable(),
    weight: z.number().optional(),
    max: z.number().nullable(),
    min: z.number().nullable(),
    page: z.number().nullable(),
    example_value: z.string().nullable(),
    not_show_if_system_name_equals: z.string().nullable(),
    show_only_if_system_name_equals: z.string().nullable(),
    form_structure_id: z.string().nullable(),
    validation_regex: validationRegexSchema,
    frontend_format: frontendFormatSchema,
    placeholder: z.string().nullable(),
    parent_form_field_id: z.string().nullable(),
    validation_sequence_step_id: z.string().nullable().optional(),
    data_modification_sequence_step_id: z.string().nullable().optional(),
    autocomplete_sequence_step_id: z.string().nullable().optional(),
    validation_scenarios: z.array(z.object({ id: z.string(), order: z.number().nullable() })).optional(),
    modification_scenarios: z.array(z.object({ id: z.string(), order: z.number().nullable() })).optional(),
    autocomplete_scenarios: z.array(z.object({ id: z.string(), order: z.number().nullable() })).optional(),
    data_modification_accessor: z.string().nullable(),
    autocomplete_accessor: z.string().nullable(),
    formFieldSelectItems: z
        .array(
            z.object({
                form_field_id: z.string().nullable(),
                id: z.string().nullable(),
                value: z.string(),
                visible_description: z.string(),
            })
        )
        .optional(),
});

export const formSchema = z.object({
    formFields: z.array(formFieldSchema).optional(),
});

export type FormStructureFormData = z.infer<typeof formSchema>;
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -
export type FormFieldsFormData = FormStructureFormData["formFields"][number];
export type FormFieldFormData = z.infer<typeof formFieldSchema>;
export type FormFieldSelectItemsFormData = FormFieldsFormData["formFieldSelectItems"][number];
